import React, { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";

const FIELDS = [
  ["points_rate_inr", "Points → INR rate", "1 point equals how many ₹"],
  ["points_per_litre", "Points per litre (reward rule)", "Default reward = pack size × this"],
  ["upi_min_points", "Minimum UPI points", "Below this, points stay in wallet"],
  ["gift_reservation_minutes", "Gift reservation (minutes)", "Auto-expiry of held stock"],
  ["rate_limit_validations_per_min", "Validation rate limit / min", "Per IP QR validation cap"],
];

export default function ConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <div className="py-20 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#8B0000] mx-auto" /></div>;

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/config", cfg); setCfg(data); toast.success("Configuration saved"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const clearDemo = async () => {
    if (!window.confirm("Remove all demo-labelled data? Imported & live data stays.")) return;
    try { const { data } = await api.delete("/demo-data"); toast.success(`Deleted ${data.deleted} demo records`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Configuration</h1>
      <p className="text-sm text-gray-500 mt-1 mb-6">Reward rules, thresholds & rate limits (all configurable)</p>
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
        {FIELDS.map(([k, label, hint]) => (
          <div key={k} className="flex items-center justify-between gap-4">
            <div><div className="font-semibold text-gray-900 text-sm">{label}</div><div className="text-xs text-gray-400">{hint}</div></div>
            <input data-testid={`config-${k}`} type="number" value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: +e.target.value })}
              className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm text-right outline-none focus:border-[#8B0000]" />
          </div>
        ))}
        <button data-testid="config-save-btn" onClick={save} disabled={busy} className="mt-2 bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-50 flex items-center gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save Configuration
        </button>
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="font-display font-bold text-gray-900">Demo Data</h3>
        <p className="text-sm text-gray-500 mt-1">Demo records are clearly labelled and safely clearable. Imported (real) & live data are never touched.</p>
        <button data-testid="clear-demo-btn" onClick={clearDemo} className="mt-4 flex items-center gap-2 border-2 border-red-500 text-red-600 hover:bg-red-50 font-bold px-4 py-2.5 rounded-lg">
          <Trash2 className="h-4 w-4" /> Clear Demo Data
        </button>
      </div>
    </div>
  );
}
